import { List, useTable } from "@refinedev/antd";
import { Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";

import { DATE_TIME_FORMAT, REPORT_REASONS, REPORT_STATUS } from "../labels";
import type { MessageReport } from "../types";

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Açık" },
  { value: "RESOLVED", label: "Sonuçlanan" },
  { value: "ALL", label: "Tümü" },
];

export const PARTY: Record<string, string> = { STUDENT: "Öğrenci", TUTOR: "Eğitmen" };

/**
 * /message-reports: conversations students or tutors reported, open ones oldest first
 * (permission `messages.moderate`). The messages open only on the report's page, audited.
 */
export function MessageReportsPage() {
  const navigate = useNavigate();
  const { tableProps, filters, setFilters } = useTable<MessageReport>({
    resource: "message-reports",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "status", operator: "eq", value: "OPEN" }] },
    syncWithLocation: true,
  });
  const status =
    (filters.find((item) => "field" in item && item.field === "status")?.value as string) ?? "OPEN";

  return (
    <List title="Bildirilen sohbetler">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={STATUS_OPTIONS}
          value={status}
          onChange={(value) =>
            setFilters([{ field: "status", operator: "eq", value: String(value) }], "replace")
          }
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        // The whole row opens it, not only the name.
        onRow={(record) => ({
          onClick: () => navigate(`/message-reports/${record.id}`),
          style: { cursor: "pointer" },
        })}
      >
        <Table.Column<MessageReport>
          title="Sohbet"
          key="conversation"
          render={(_, report) => (
            <Space direction="vertical" size={0}>
              <Link to={`/message-reports/${report.id}`}>
                <Typography.Text strong>
                  {report.student.name} ↔ {report.tutor.name}
                </Typography.Text>
              </Link>
              <Typography.Text type="secondary">Bildiren: {PARTY[report.reporter]}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<MessageReport>
          title="Neden"
          dataIndex="reason"
          render={(reason: string, report) => (
            <Space direction="vertical" size={0}>
              {REPORT_REASONS[reason]}
              {report.note ? (
                <Typography.Text type="secondary" ellipsis style={{ maxWidth: 320 }}>
                  {report.note}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<MessageReport>
          title="Tarih"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_TIME_FORMAT)}
        />
        <Table.Column<MessageReport>
          title="Durum"
          dataIndex="status"
          render={(value: string, report) => (
            <Space size={4}>
              <Tag color={REPORT_STATUS[value]?.color}>{REPORT_STATUS[value]?.label}</Tag>
              {report.closedAt ? <Tag color="red">Sohbet kapalı</Tag> : null}
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
